import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import Button from './Button';

const SIZES = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    // Wide enough for an editable grid without horizontal scrolling.
    xxl: 'max-w-5xl',
    full: 'max-w-6xl',
};

/**
 * One modal structure for the whole app: header (title / subtitle / close),
 * scrollable body, footer with cancel + a single primary action.
 * Traps focus, restores it on close, closes on Escape and overlay click.
 */
const Modal = ({
    isOpen,
    onClose,
    title,
    subtitle,
    children,
    footer,
    size = 'md',
    closeOnOverlay = true,
    className = '',
    bodyClassName = '',
}) => {
    const panelRef = useRef(null);
    const restoreRef = useRef(null);
    const titleId = useId();

    useEffect(() => {
        if (!isOpen) return undefined;

        restoreRef.current = document.activeElement;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const focusables = () =>
            Array.from(
                panelRef.current?.querySelectorAll(
                    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
                ) || []
            ).filter((el) => el.offsetParent !== null);

        const timer = window.setTimeout(() => {
            const list = focusables();
            (list.find((el) => !el.dataset.autofocusSkip) || panelRef.current)?.focus();
        }, 0);

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose?.();
                return;
            }
            if (e.key !== 'Tab') return;
            const list = focusables();
            if (!list.length) return;
            const first = list[0];
            const last = list[list.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => {
            window.clearTimeout(timer);
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = prevOverflow;
            restoreRef.current?.focus?.();
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-modal flex items-start sm:items-center justify-center p-4 overflow-y-auto"
            role="presentation"
        >
            <div
                className="fixed inset-0 animate-fade-in"
                style={{ background: 'var(--bg-overlay)' }}
                onClick={closeOnOverlay ? onClose : undefined}
                aria-hidden="true"
            />
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
                tabIndex={-1}
                className={`relative w-full ${SIZES[size] || SIZES.md} bg-white rounded-lg shadow-lg my-auto animate-scale-in outline-none flex flex-col max-h-[calc(100vh-2rem)] ${className}`}
            >
                {(title || onClose) && (
                    <div className="flex items-start justify-between gap-4 px-5 py-3.5 border-b border-[var(--divider-color)] shrink-0">
                        <div className="min-w-0">
                            {title && (
                                <h2 id={titleId} className="text-lg font-semibold text-ink-900 truncate">
                                    {title}
                                </h2>
                            )}
                            {subtitle && <p className="text-label text-ink-500 mt-0.5">{subtitle}</p>}
                        </div>
                        {onClose && (
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label="Close dialog"
                                data-autofocus-skip="true"
                                className="shrink-0 -mr-1 -mt-0.5 p-1.5 rounded text-ink-500 hover:text-ink-800 hover:bg-ink-100 transition-colors duration-150"
                            >
                                <X size={17} aria-hidden="true" />
                            </button>
                        )}
                    </div>
                )}

                <div className={`px-5 py-4 overflow-y-auto grow ${bodyClassName}`}>{children}</div>

                {footer && (
                    <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[var(--divider-color)] bg-[var(--bg-subtle)] rounded-b-lg shrink-0">
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

/** Destructive/confirming dialog built on the same shell. */
export const ConfirmDialog = ({
    isOpen,
    onClose,
    onConfirm,
    title = 'Are you sure?',
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'danger',
    isLoading = false,
}) => (
    <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        size="sm"
        footer={
            <>
                <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                    {cancelText}
                </Button>
                <Button variant={variant} onClick={onConfirm} loading={isLoading}>
                    {confirmText}
                </Button>
            </>
        }
    >
        <p className="text-md text-ink-600 leading-relaxed">{message}</p>
    </Modal>
);

export default Modal;
